import { gitchaBioUrl } from "@/hooks/useAgentBioLink";
export const RESULTS_PATH = "/analytics";
export const LEGACY_RESULTS_PATH = "/results";

export const SOURCE_LABELS = {
  google: "Google",
  bing: "Bing",
  chatgpt: "ChatGPT",
  perplexity: "Perplexity",
  claude: "Claude",
  gemini: "Gemini",
  copilot: "Microsoft Copilot",
  website: "Your website",
  gitcha: "Gitcha.com",
  facebook: "Facebook",
  instagram: "Instagram",
  linkedin: "LinkedIn",
  x: "X",
  messages: "Text messages",
  email: "Email",
  other: "Other sites",
  unknown: "Not recorded",
};

const CAME_FROM = {
  google: "came from Google",
  bing: "came from Bing",
  chatgpt: "came from ChatGPT",
  perplexity: "came from Perplexity",
  claude: "came from Claude",
  gemini: "came from Gemini",
  copilot: "came from Microsoft Copilot",
  website: "came from your website",
  gitcha: "found you on Gitcha.com",
  facebook: "came from Facebook",
  instagram: "came from Instagram",
  linkedin: "came from LinkedIn",
  x: "came from X",
  messages: "came from a text message",
  email: "came from an email",
};

export const sourceLabel = (source) => SOURCE_LABELS[source] || SOURCE_LABELS.other;

export const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

export const fullName = (person) =>
  [person?.firstName, person?.lastName].filter(Boolean).join(" ").trim();

const withCompany = (person) => {
  const name = fullName(person) || "An agent";
  return person?.company ? `${name} (${person.company})` : name;
};

const quoted = (title) => (title ? `"${title}"` : null);

export const changeShort = (change) => {
  const current = Number(change?.current) || 0;
  const previous = Number(change?.previous) || 0;
  const diff = current - previous;
  if (current === 0 && previous === 0) return null;
  if (diff === 0) return { text: "Same", direction: "flat" };
  return { text: `${diff > 0 ? "+" : "−"}${Math.abs(diff)}`, direction: diff > 0 ? "up" : "down" };
};

export const usualLine = (change, days) => {
  const current = Number(change?.current) || 0;
  const previous = Number(change?.previous) || 0;
  if (current === 0 && previous === 0) return null;
  const diff = current - previous;
  if (diff === 0) return { text: `Same as the previous ${days} days`, direction: "flat" };
  return {
    text: `${diff > 0 ? "Up" : "Down"} from ${previous} in the previous ${days} days`,
    direction: diff > 0 ? "up" : "down",
  };
};

export const glue = (text) => text.replace(/ (\S+)$/, "\u00a0$1");

export const compareLine = (change, days) => {
  const current = Number(change?.current) || 0;
  const previous = Number(change?.previous) || 0;
  const diff = current - previous;
  if (diff === 0) return `Same as the ${days} days before.`;
  return `${diff > 0 ? "Up" : "Down"} from ${previous} in the ${days} days before.`;
};

export const networkFeedLine = (item, networkName = "LAR") => {
  if (item.kind === "intro_received") {
    return {
      title: `${withCompany(item.agent)} sent you an introduction${item.address ? ` for ${item.address}` : ""}`,
      detail: [quoted(item.wantListing?.title) && `For your Buyer Want-Listing ${quoted(item.wantListing.title)}`, `${networkName} Buyer Listing Service`]
        .filter(Boolean)
        .join(" · "),
    };
  }
  if (item.kind === "intro_replied") {
    return {
      title: `${withCompany(item.agent)} replied to your introduction${item.address ? ` for ${item.address}` : ""}`,
      detail: [quoted(item.wantListing?.title) && `Their Buyer Want-Listing ${quoted(item.wantListing.title)}`, `${networkName} Buyer Listing Service`]
        .filter(Boolean)
        .join(" · "),
    };
  }
  if (item.kind === "message") {
    return {
      title: `${withCompany(item.agent)} sent you a message`,
      detail: `${networkName} Buyer Listing Service`,
    };
  }
  if (item.kind === "match") {
    const place = item.address || [item.city, item.state].filter(Boolean).join(", ");
    return {
      title: `New listing matches your Buyer Want-Listing: ${place || "an active listing"}`,
      detail: [quoted(item.wantListing?.title), "High-ranking match"].filter(Boolean).join(" · "),
    };
  }
  return { title: "", detail: "" };
};

const TAP_VERBS = {
  call: "tapped Call on your Gitcha bio",
  text: "tapped Text on your Gitcha bio",
  website: "went to your website from your Gitcha bio",
};

export const gitchaFeedLine = (item) => {
  const who = item.name || "Someone";
  const fromWebsite = item.source === "showcase" || item.channel === "website";
  const origin = fromWebsite ? CAME_FROM.website : CAME_FROM[item.channel] || null;
  let title;
  if (item.contactType === "demand_check") {
    title = `${who} checked buyer demand for their home`;
  } else if (item.contactType === "buyer_form") {
    title = item.intent === "sell" ? `${who} asked you about selling their home` : `${who} filled out your Buyer Listing Form`;
  } else if (item.contactType === "message" || item.contactType === "email") {
    const about = quoted(item.wantListing?.title);
    title = about
      ? item.intent === "owner"
        ? `${who} owns a property for your Buyer Want-Listing ${about}`
        : `${who} contacted you about your Buyer Want-Listing ${about}`
      : `${who} sent you a message`;
  } else if (item.contactType === "social") {
    title = `Someone opened your ${SOURCE_LABELS[item.intent] || "link"} from your Gitcha bio`;
  } else {
    title = `Someone ${TAP_VERBS[item.contactType] || "contacted you from your Gitcha bio"}`;
  }
  const inTitle = item.contactType === "message" || item.contactType === "email";
  const detail = [
    inTitle ? null : quoted(item.wantListing?.title),
    item.intent === "owner" ? item.address : null,
    origin ? origin.charAt(0).toUpperCase() + origin.slice(1) : "From Gitcha",
  ]
    .filter(Boolean)
    .join(" · ");
  return { title, detail };
};

const FOUND_THROUGH = {
  google: "Google",
  bing: "Bing",
  chatgpt: "ChatGPT",
  perplexity: "Perplexity",
  claude: "Claude",
  gemini: "Gemini",
  copilot: "Microsoft Copilot",
  facebook: "Facebook",
  instagram: "Instagram",
  linkedin: "LinkedIn",
  x: "X",
  email: "an email",
  messages: "a text message",
};

export const foundThrough = (channel, source) => {
  if (source === "showcase" || channel === "website") return "They came from your website.";
  return FOUND_THROUGH[channel] ? `They found you through ${FOUND_THROUGH[channel]}.` : "";
};

const shortDate = (value) => {
  if (!value) return null;
  const date = new Date(`${String(value).slice(0, 10)}T12:00:00Z`);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
};

const money = (value) =>
  value ? `$${Number(value).toLocaleString("en-US", { maximumFractionDigits: 0 })}` : null;

export const evidenceLine = (event) => {
  const when = shortDate(event.at);
  const lines = {
    match: `Matched your Buyer Want-Listing${event.wantAdTitle ? ` "${event.wantAdTitle}"` : ""}`,
    opened: "You opened the match",
    introduction: "Introduction between you and the other agent",
    conversation: "You and the other agent messaged each other",
    message: "Message between you and the other agent",
    contract: "Under contract",
    closed: `Closed${event.price ? ` at ${money(event.price)}` : ""}`,
    form: `They filled out your Buyer Listing Form${CAME_FROM[event.channel] ? ` (${CAME_FROM[event.channel]})` : ""}`,
    published: "You published their Buyer Want-Listing",
    seller_form: "They asked you about selling",
    listed: "Their home went on the market with you",
  };
  const text = lines[event.type];
  return text ? { when, text } : null;
};

export const winTitle = (win) => {
  if (win.kind === "client") return win.wantListing?.title ? `New client: "${win.wantListing.title}"` : "New client from your Buyer Listing Form";
  return win.address || "A property in your network";
};

export const winMeta = (win) => {
  if (win.kind === "client") return "From Gitcha";
  const status = win.closeDate ? `Closed ${shortDate(win.closeDate)}` : win.contractDate ? `Under contract ${shortDate(win.contractDate)}` : null;
  const role = win.kind === "buy_side" ? "You were the buyer's agent" : win.kind === "list_side" ? "You were the listing agent" : "Your listing";
  return [status, money(win.closePrice), role].filter(Boolean).join(" · ");
};

export const winReason = (win, networkName = "LAR") => {
  const service = `${networkName} BLS`;
  const types = (win.evidence || []).map((event) => event.type);
  const other = fullName(win.counterpart);
  if (win.source === "gitcha") {
    return win.kind === "listing"
      ? "Counted as a Gitcha win because the seller first reached out to you through Gitcha."
      : "Counted as a Gitcha win because their Buyer Listing Form became a published Buyer Want-Listing.";
  }
  if (types.includes("introduction") || types.includes("conversation")) {
    return `Counted as a ${service} win because you and ${other || "the other agent"} connected there before the contract.`;
  }
  if (types.includes("opened")) return `Counted as a ${service} win because you opened this match before the contract.`;
  if (types.includes("message")) return `Counted as a ${service} win because of a message there before the contract.`;
  return `This matched your Buyer Want-Listing before the contract.`;
};

export const askQuestion = (win, networkName = "LAR") =>
  `Did the ${networkName} BLS help with ${win.address || "this sale"}?`;

export const shareText = (win, networkName = "LAR") =>
  `${win.closeDate ? "Closed" : "Under contract"}: ${win.address}, through the ${networkName} Buyer Listing Service.`;

export const ownBioHref = (slug) => {
  if (!slug) return null;
  const base = gitchaBioUrl(slug) || `/agent-info/${encodeURIComponent(slug)}`;
  return `${base}?self=1`;
};
